import {
    PERSON_FIELDS,
    personFieldIds,
    personFieldNames
} from '@ast-data/shared';
import { personFieldLabelId, type LinkedDoc } from '../users/useUserLinks';
import type { CrewKeyResolver } from './crewIdentity';

/** A record that names a crew member, and in which field. */
export interface CrewRecord {
    /** `entity.name`. */
    entityType: string;
    doc: LinkedDoc;
    /** i18n id of the field naming the person. */
    kindId: string;
}

/** One person found on the records, under their crew key. */
export interface CollectedPerson {
    /** The name as first written — `displayName` turns a linked one into the account's. */
    name: string;
    /** Source keys for `crew.source.*`. */
    sources: Set<string>;
    records: CrewRecord[];
}

/** The `crew.source.*` key for each kind of record. */
const SOURCE_KEYS: Record<string, string> = {
    project: 'project',
    timeEntry: 'timeEntry',
    diary: 'diary',
    findingPlanum: 'planum',
    findingProfile: 'profile',
    overallDescription: 'overallDescription',
    areaDescription: 'areaDescription',
    drawingSheet: 'drawingSheet',
    photo: 'photo',
    find: 'find',
    sample: 'sample'
};

/**
 * Everyone the records name, by crew key — every person field of every record
 * (shared PERSON_FIELDS), each name with its positional id ('' for free text),
 * so a person who IS a user collapses onto one entry across all of them. What
 * the Grabungsmannschaft and the Tagebuch's day both read their people from.
 */
export function collectCrew(
    recordsByType: readonly (readonly [string, readonly object[]])[],
    resolver: CrewKeyResolver
): Map<string, CollectedPerson> {
    const collected = new Map<string, CollectedPerson>();
    const add = (
        rawName: unknown,
        storedId: string | undefined,
        entityType: string,
        doc: LinkedDoc,
        kindId: string
    ) => {
        // Coerce defensively — a malformed record (e.g. a legacy
        // double-wrapped person array) must never crash the crew lists.
        const name = (
            typeof rawName === 'string' ? rawName : String(rawName ?? '')
        ).trim();
        const key = resolver.keyForName(name, storedId);
        if (!key) {
            return;
        }
        const source = SOURCE_KEYS[entityType] ?? entityType;
        const existing = collected.get(key);
        if (!existing) {
            collected.set(key, {
                name,
                sources: new Set([source]),
                records: [{ entityType, doc, kindId }]
            });
            return;
        }
        existing.sources.add(source);
        if (!existing.name && name) {
            existing.name = name;
        }
        // One entry per record and field, however often the field names them.
        const last = existing.records[existing.records.length - 1];
        if (last?.doc !== doc || last.kindId !== kindId) {
            existing.records.push({ entityType, doc, kindId });
        }
    };

    for (const [entityType, docs] of recordsByType) {
        for (const record of docs) {
            const doc = record as LinkedDoc;
            for (const field of PERSON_FIELDS[entityType] ?? []) {
                if (field.appliesTo && !field.appliesTo(doc)) {
                    continue;
                }
                const ids = personFieldIds(field.shape, doc[field.id]);
                const kindId = personFieldLabelId(entityType, field.name);
                personFieldNames(field.shape, doc[field.name]).forEach(
                    (name, index) =>
                        add(name, ids[index], entityType, doc, kindId)
                );
            }
        }
    }
    return collected;
}
